import { isValidPhoneNumber } from "libphonenumber-js";
import { z } from "zod";
import { woodboxSchema } from "@/types/forms/home/cotationFormData.form";
import { FedexServiceEnum } from "@/constants/enums/fedexService.enum";
import { FedexPackagingEnum } from "@/constants/enums/fedexPackaging.enum";
import { FedexSignatureEnum } from "@/constants/enums/fedexSignature.enum";
import { WineColorEnum } from "@/constants/enums/wineColor.enum";
import { CustomQuotationTypeEnum } from "@/constants/enums/customQuotationType.enum";
import { ParcelProductTypeEnum } from "@/constants/enums/parcelProductType.enum";
import { OtherCustomQuotationTypeEnum } from "@/constants/enums/otherCustomQuotationType.enum";
import { countryRequiresPostalCode } from "@/lib/postal-codes.util";
import { isFedexFreightServiceType } from "@/lib/fedex-service-type";
import {
  calculateDeliveryContentMinimumWeightKg,
  calculateExpeditionPackagingTotalWeightKg,
} from "@/lib/fedex-weight.util";
import { roundNumberToFixed } from "@/lib/money.util";
import { isBefore, startOfDay } from "date-fns";
import { i18nMessage } from "@/lib/i18nMessage";

export const DeliveryContentSchema = z
  .object({
    wineName: z.string().trim().min(1, i18nMessage("validation:required")).max(100),
    year: z.string().trim().optional(),
    alcoholPercent: z
      .number({ message: i18nMessage("validation:required"), coerce: true })
      .min(0, i18nMessage("validation:minValue", { min: 0 }))
      .max(100, i18nMessage("boExpeditionForm:expeditionValidation.alcoholPercentMax")),
    volume: z.coerce
      .number({ message: i18nMessage("validation:required"), coerce: true })
      .min(0, i18nMessage("validation:minValue", { min: 0 })),
    color: z.nativeEnum(WineColorEnum, { message: i18nMessage("validation:required") }),
    numberOfBottles: z.coerce
      .number({ message: i18nMessage("validation:required"), coerce: true })
      .min(1, i18nMessage("validation:minValue", { min: 1 })),
    unitPrice: z.coerce
      .number({ message: i18nMessage("validation:required"), coerce: true })
      .min(0, i18nMessage("validation:minValue", { min: 0 })),
    productId: z.string().optional(),
    productUSAId: z.string().optional(),
    isEditing: z.boolean().optional(),
  })
  .superRefine((data, ctx) => {
    if (!data.productId && !data.productUSAId) {
      const message = i18nMessage("boExpeditionForm:expeditionValidation.productRequired");
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message,
        path: ["productId"],
      });
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message,
        path: ["productUSAId"],
      });
    }
  });

export const FREIGHT_WEIGHT_THRESHOLD_KG = 68;

export const ExpeditionPackagingSchema = z
  .object({
    count: z.coerce
      .number({ message: i18nMessage("validation:required"), coerce: true })
      .min(1, i18nMessage("validation:minValue", { min: 1 })),
    weight: z.coerce
      .number({ message: i18nMessage("validation:required"), coerce: true })
      .min(1, i18nMessage("validation:minValue", { min: 1 })),
    length: z.coerce
      .number({ message: i18nMessage("validation:required"), coerce: true })
      .min(0, i18nMessage("validation:minValue", { min: 0 }))
      .optional(),
    width: z.coerce
      .number({ message: i18nMessage("validation:required"), coerce: true })
      .min(0, i18nMessage("validation:minValue", { min: 0 }))
      .optional(),
    height: z.coerce
      .number({ message: i18nMessage("validation:required"), coerce: true })
      .min(0, i18nMessage("validation:minValue", { min: 0 }))
      .optional(),
    value: z.coerce
      .number({ message: i18nMessage("validation:required"), coerce: true })
      .min(0, i18nMessage("validation:minValue", { min: 0 })),
  })
  .superRefine((data, ctx) => {
    if (data.weight <= FREIGHT_WEIGHT_THRESHOLD_KG) return;

    (["length", "width", "height"] as const).forEach((field) => {
      const value = data[field];
      if (typeof value !== "number" || Number.isNaN(value)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: i18nMessage(
            "boExpeditionForm:expeditionValidation.dimensionRequiredHeavyParcel",
          ),
          path: [field],
        });
      }
    });
  });

export const ShipperInfoSchema = z
  .object({
    contactName: z
      .string()
      .trim()
      .min(1, i18nMessage("boExpeditionForm:expeditionValidation.contactNameRequired")),
    companyName: z
      .string()
      .trim()
      .max(35, i18nMessage("validation:maxLength", { max: 35 }))
      .optional(),
    streetLines: z
      .array(
        z
          .string()
          .trim()
          .max(35, i18nMessage("validation:maxLength", { max: 35 })),
      )
      .min(1, i18nMessage("boExpeditionForm:expeditionValidation.streetLinesRequired"))
      .superRefine((lines, ctx) => {
        if (!lines[0]?.length) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: i18nMessage("validation:required"),
            path: [0],
          });
        }
        lines.forEach((line, index) => {
          if (line.length > 0 && line.length < 5) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: i18nMessage("validation:minLength", { min: 5 }),
              path: [index],
            });
          }
        });
      }),
    city: z
      .string()
      .trim()
      .min(1, i18nMessage("boExpeditionForm:expeditionValidation.cityRequired"))
      .max(35, i18nMessage("validation:maxLength", { max: 35 })),
    stateOrProvinceCode: z.string().trim().optional(),
    postalCode: z.string().trim().optional(),
    countryCode: z
      .string()
      .trim()
      .min(1, i18nMessage("boExpeditionForm:expeditionValidation.countryCodeRequired"))
      .max(2, i18nMessage("validation:maxLength", { max: 2 })),
    email: z
      .string()
      .trim()
      .email({ message: i18nMessage("validation:invalidEmail") }),
    phone: z
      .string()
      .trim()
      .refine((mobile) => isValidPhoneNumber(mobile), {
        message: i18nMessage("boExpeditionForm:expeditionValidation.mobilePhoneInvalid"),
      }),
    taxId: z.string().trim().optional(),
    residential: z.boolean().default(false),
  })
  .superRefine((data, ctx) => {
    if (
      (data.countryCode === "CA" || data.countryCode === "US" || data.countryCode === "IN") &&
      (!data.stateOrProvinceCode || data.stateOrProvinceCode.length !== 2)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: i18nMessage("boExpeditionForm:expeditionValidation.shipperStateRequired"),
        path: ["stateOrProvinceCode"],
      });
    }
    if (countryRequiresPostalCode(data.countryCode)) {
      if (!data.postalCode) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: i18nMessage("boExpeditionForm:expeditionValidation.postalCodeRequired"),
          path: ["postalCode"],
        });
      }
    }
  });

export const MANUFACTURER_COUNTRY_CODE = "FR";

export const isManufacturerInfoRequiredFor = (
  billingEntityCountryCode: string | undefined,
  destinationCountryCode: string | undefined,
) =>
  !!billingEntityCountryCode &&
  billingEntityCountryCode !== MANUFACTURER_COUNTRY_CODE &&
  destinationCountryCode === "US";

export const ManufacturerInfoSchema = z.object({
  companyName: z
    .string()
    .trim()
    .min(1, i18nMessage("validation:required"))
    .max(35, i18nMessage("validation:maxLength", { max: 35 })),
  streetLines: z
    .array(
      z
        .string()
        .trim()
        .max(35, i18nMessage("validation:maxLength", { max: 35 })),
    )
    .min(1, i18nMessage("boExpeditionForm:expeditionValidation.streetLinesRequired"))
    .superRefine((lines, ctx) => {
      if (!lines[0]?.length) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: i18nMessage("validation:required"),
          path: [0],
        });
      }
    }),
  city: z
    .string()
    .trim()
    .min(1, i18nMessage("boExpeditionForm:expeditionValidation.cityRequired"))
    .max(35, i18nMessage("validation:maxLength", { max: 35 })),
  postalCode: z
    .string()
    .trim()
    .min(1, i18nMessage("boExpeditionForm:expeditionValidation.postalCodeRequired")),
  countryCode: z.literal(MANUFACTURER_COUNTRY_CODE),
});
export const ExpeditionFedexApiSchema = z
  .object({
    expeditionPackaging: z.array(ExpeditionPackagingSchema),
    deliveryInstructions: z.string().optional().default(""),
    serviceType: z.nativeEnum(FedexServiceEnum, { message: i18nMessage("validation:required") }),
    packagingType: z.nativeEnum(FedexPackagingEnum, {
      message: i18nMessage("validation:required"),
    }),
    signatureType: z
      .nativeEnum(FedexSignatureEnum, { message: i18nMessage("validation:required") })
      .optional()
      .default(FedexSignatureEnum.NONE),
    shipDate: z.coerce
      .date({ message: i18nMessage("boExpeditionForm:expeditionValidation.shipDateRequired") })
      .refine(
        (date) => {
          return !isBefore(startOfDay(date), startOfDay(new Date()));
        },
        { message: i18nMessage("boExpeditionForm:expeditionValidation.shipDateInvalid") },
      ),
    shippingReference: z.string({
      message: i18nMessage("boExpeditionForm:expeditionValidation.shippingReferenceRequired"),
    }),
    fedexAccountId: z.string({
      message: i18nMessage("boExpeditionForm:expeditionValidation.fedexAccountRequired"),
    }),
    fedexRate: z.any({
      message: i18nMessage("boExpeditionForm:expeditionValidation.fedexRateRequired"),
    }),
  })
  .superRefine((data, ctx) => {
    if (!isFedexFreightServiceType(data.serviceType)) return;
    data.expeditionPackaging.forEach((pkg, index) => {
      (["length", "width", "height"] as const).forEach((field) => {
        const value = pkg[field];
        if (typeof value !== "number" || Number.isNaN(value)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: i18nMessage("boExpeditionForm:expeditionValidation.dimensionRequiredFreight"),
            path: ["expeditionPackaging", index, field],
          });
        }
      });
    });
  });

export const ExpeditionWoApiSchema = z.object({
  packageCount: z.coerce
    .number({ message: i18nMessage("validation:required"), coerce: true })
    .min(1, i18nMessage("validation:minValue", { min: 1 })),
  weight: z.coerce
    .number({ message: i18nMessage("validation:required"), coerce: true })
    .min(0, i18nMessage("validation:minValue", { min: 0 })),
});

export const ExpeditionOptionsSchema = z
  .object({
    dapOption: z.boolean(),
    express: z.boolean(),
    insurance: z.boolean(),
    sample: z.boolean(),
    dae: z.boolean(),
    mrn: z.boolean(),
    signature: z.boolean(),
    packagingPriority: z.boolean(),
    remotePickup: z.boolean(),
    includeCompanyName: z.boolean(),
    handDelivery: z.boolean(),
    sampleText: z.string().optional().default(""),
    daeNumbers: z.array(z.string().trim()).optional().default([]),
    mrnNumbers: z.array(z.string().trim()).optional().default([]),
    presonalUseToggle: z.boolean(),
    personalUse: z.string().optional().default(""),
  })
  .superRefine((data, ctx) => {
    if (data.sample && (!data.sampleText || data.sampleText.trim().length === 0)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: i18nMessage("boExpeditionForm:expeditionValidation.sampleTextRequired"),
        path: ["sampleText"],
      });
    }
    if (data.dae && data.daeNumbers.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: i18nMessage("boExpeditionForm:expeditionValidation.daeNumbersRequired"),
        path: ["daeNumbers"],
      });
    }
    if (data.dae && data.mrn && !data.mrnNumbers[0]) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: i18nMessage("boExpeditionForm:expeditionValidation.mrnNumbersRequired"),
        path: ["mrnNumbers", 0],
      });
    }
  });

export const ExpeditionDeliveryInfoSchema = z
  .object({
    company: z
      .string()
      .trim()
      .max(35, i18nMessage("validation:maxLength", { max: 35 }))
      .optional(),
    lastName: z
      .string()
      .trim()
      .min(1, i18nMessage("boExpeditionForm:expeditionValidation.contactNameRequired")),
    firstName: z
      .string()
      .trim()
      .min(1, i18nMessage("boExpeditionForm:expeditionValidation.contactFirstNameRequired")),
    email: z
      .string()
      .trim()
      .email({ message: i18nMessage("validation:invalidEmail") }),
    phone: z
      .string({
        message: i18nMessage("boExpeditionForm:expeditionValidation.contactMobilePhoneRequired"),
      })
      .trim()
      .refine((mobile) => isValidPhoneNumber(mobile), {
        message: i18nMessage("boExpeditionForm:expeditionValidation.mobilePhoneInvalid"),
      }),
    street: z
      .string()
      .trim()
      .min(5, i18nMessage("validation:minLength", { min: 5 }))
      .max(35, i18nMessage("validation:maxLength", { max: 35 })),
    additionalInfo: z
      .string()
      .trim()
      .min(5, i18nMessage("validation:minLength", { min: 5 }))
      .max(35, i18nMessage("validation:maxLength", { max: 35 }))
      .optional()
      .or(z.literal("")),
    additionalInfo2: z
      .string()
      .trim()
      .min(5, i18nMessage("validation:minLength", { min: 5 }))
      .max(35, i18nMessage("validation:maxLength", { max: 35 }))
      .optional()
      .or(z.literal("")),
    postalCode: z.string().trim().optional(),
    city: z
      .string()
      .trim()
      .min(1, i18nMessage("boExpeditionForm:expeditionValidation.cityRequired"))
      .max(35, i18nMessage("validation:maxLength", { max: 35 })),
    countryCode: z
      .string()
      .trim()
      .min(1, i18nMessage("boExpeditionForm:expeditionValidation.countryCodeRequired")),
    stateOrProvinceCode: z.string().trim().optional(),
    additionalEmail: z
      .string()
      .trim()
      .email({ message: i18nMessage("validation:invalidEmail") })
      .optional()
      .or(z.literal("")),
    additionalPhone: z.string().trim().optional(),
    clientAdditionalInfo: z.array(z.string().trim()).optional().default([]),
  })
  .superRefine((data, ctx) => {
    if (
      (data.countryCode === "CA" || data.countryCode === "US") &&
      (!data.stateOrProvinceCode || data.stateOrProvinceCode.length !== 2)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: i18nMessage("boExpeditionForm:expeditionValidation.deliveryStateRequired"),
        path: ["stateOrProvinceCode"],
      });
    }
    if (countryRequiresPostalCode(data.countryCode)) {
      if (!data.postalCode) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: i18nMessage("boExpeditionForm:expeditionValidation.postalCodeRequired"),
          path: ["postalCode"],
        });
      }
    }
  });

const ExpeditionUpdateBoSharedFields = {
  deliveryInfo: ExpeditionDeliveryInfoSchema,
  shipperInfo: ShipperInfoSchema,
  billingEntityId: z
    .string()
    .min(1, i18nMessage("boExpeditionForm:expeditionValidation.billingEntityRequired")),
  comment: z.string().optional(),
  reference: z.string().trim().optional(),
  options: ExpeditionOptionsSchema,
  importerId: z.string().optional(),
  customId: z.string().min(1, i18nMessage("boExpeditionForm:expeditionValidation.customRequired")),
};

const QuotationUpdateBoBaseFields = {
  ...ExpeditionUpdateBoSharedFields,
  pickUpLocationId: z
    .string()
    .min(1, i18nMessage("boExpeditionForm:expeditionValidation.pickupLocationRequired")),
  deliveryContent: z
    .array(DeliveryContentSchema)
    .min(1, i18nMessage("boExpeditionForm:expeditionValidation.productsRequired")),
  quotationWoodBox: z.array(woodboxSchema),
  updatePriceParameters: z.boolean().optional(),
};

const validateFedexPackagingWeightAgainstContent = (
  data: {
    apiEnum: "FEDEX" | "NONE";
    deliveryContent: { numberOfBottles: number; volume: number }[];
    expeditionFedexApi?: { expeditionPackaging: { weight: number; count: number }[] };
  },
  ctx: z.RefinementCtx,
) => {
  if (data.apiEnum !== "FEDEX" || !data.expeditionFedexApi) return;

  const totalWeight = calculateExpeditionPackagingTotalWeightKg(
    data.expeditionFedexApi.expeditionPackaging,
  );
  const minimumWeight =
    Math.round(calculateDeliveryContentMinimumWeightKg(data.deliveryContent) * 100) / 100;

  if (totalWeight < minimumWeight) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: i18nMessage("boExpeditionForm:expeditionValidation.packagingWeightBelowContent", {
        totalWeight: roundNumberToFixed(totalWeight, 2),
        minimumWeight: roundNumberToFixed(minimumWeight, 2),
      }),
      path: ["expeditionFedexApi", "expeditionPackaging"],
    });
  }
};

const validateInsuranceValueAgainstContent = (
  data: {
    apiEnum: "FEDEX" | "NONE";
    deliveryContent: { numberOfBottles: number; unitPrice: number }[];
    expeditionFedexApi?: { expeditionPackaging: { value: number; count: number }[] };
  },
  ctx: z.RefinementCtx,
) => {
  if (data.apiEnum !== "FEDEX" || !data.expeditionFedexApi) return;

  const totalInsuranceValue = data.expeditionFedexApi.expeditionPackaging.reduce(
    (acc, pkg) => acc + Number(pkg.value || 0) * Number(pkg.count || 0),
    0,
  );
  const totalProductsValue = data.deliveryContent.reduce(
    (acc, product) => acc + Number(product.unitPrice || 0) * Number(product.numberOfBottles || 0),
    0,
  );

  if (totalInsuranceValue > totalProductsValue) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: i18nMessage("boExpeditionForm:expeditionValidation.insuranceValueAboveProducts", {
        totalInsuranceValue: roundNumberToFixed(totalInsuranceValue, 2),
        totalProductsValue: roundNumberToFixed(totalProductsValue, 2),
      }),
      path: ["expeditionFedexApi", "expeditionPackaging"],
    });
  }
};

export const QuotationUpdateBoFormSchema = z
  .discriminatedUnion("apiEnum", [
    z.object({
      ...QuotationUpdateBoBaseFields,
      apiEnum: z.literal("NONE"),
      expeditionWoApi: ExpeditionWoApiSchema,
    }),
    z.object({
      ...QuotationUpdateBoBaseFields,
      apiEnum: z.literal("FEDEX"),
      manufacturerInfo: ManufacturerInfoSchema.optional(),
      expeditionFedexApi: ExpeditionFedexApiSchema,
    }),
  ])
  .superRefine((data, ctx) => {
    validateFedexPackagingWeightAgainstContent(data, ctx);
    validateInsuranceValueAgainstContent(data, ctx);
  });

const ParcelPackageSchema = z.object({
  weight: z.coerce
    .number({ message: i18nMessage("validation:required"), coerce: true })
    .min(0, i18nMessage("validation:minValue", { min: 0 })),
  length: z.coerce
    .number({ message: i18nMessage("validation:required"), coerce: true })
    .min(0, i18nMessage("validation:minValue", { min: 0 })),
  width: z.coerce
    .number({ message: i18nMessage("validation:required"), coerce: true })
    .min(0, i18nMessage("validation:minValue", { min: 0 })),
  height: z.coerce
    .number({ message: i18nMessage("validation:required"), coerce: true })
    .min(0, i18nMessage("validation:minValue", { min: 0 })),
  value: z.coerce
    .number({ message: i18nMessage("validation:required"), coerce: true })
    .min(0, i18nMessage("validation:minValue", { min: 0 })),
});

const ParcelProductSchema = z
  .object({
    type: z.nativeEnum(ParcelProductTypeEnum, { message: i18nMessage("validation:required") }),
    description: z.string().trim().optional(),
    volume: z.coerce.number({ coerce: true }).optional(),
    bottleQuantity: z.coerce.number({ coerce: true }).optional(),
  })
  .superRefine((data, ctx) => {
    if (data.type === ParcelProductTypeEnum.BOTTLE) {
      if (typeof data.volume !== "number" || Number.isNaN(data.volume)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: i18nMessage("boExpeditionForm:expeditionValidation.volumeRequired"),
          path: ["volume"],
        });
      }
      if (typeof data.bottleQuantity !== "number" || Number.isNaN(data.bottleQuantity)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: i18nMessage("boExpeditionForm:expeditionValidation.bottleQuantityRequired"),
          path: ["bottleQuantity"],
        });
      }
    }
  });

const ParcelDetailSchema = z.object({
  parcelCount: z.coerce
    .number({ message: i18nMessage("validation:required"), coerce: true })
    .min(1, i18nMessage("validation:minValue", { min: 1 })),
  parcelPackages: z
    .array(ParcelPackageSchema)
    .min(1, i18nMessage("boExpeditionForm:expeditionValidation.parcelPackagesRequired")),
  parcelProducts: z
    .array(ParcelProductSchema)
    .min(1, i18nMessage("boExpeditionForm:expeditionValidation.productsRequired")),
});

const PalettePackageSchema = z.object({
  length: z.coerce
    .number({ message: i18nMessage("validation:required"), coerce: true })
    .min(0, i18nMessage("validation:minValue", { min: 0 })),
  width: z.coerce
    .number({ message: i18nMessage("validation:required"), coerce: true })
    .min(0, i18nMessage("validation:minValue", { min: 0 })),
  height: z.coerce
    .number({ message: i18nMessage("validation:required"), coerce: true })
    .min(0, i18nMessage("validation:minValue", { min: 0 })),
  weight: z.coerce
    .number({ message: i18nMessage("validation:required"), coerce: true })
    .min(0, i18nMessage("validation:minValue", { min: 0 })),
  value: z.coerce
    .number({ message: i18nMessage("validation:required"), coerce: true })
    .min(0, i18nMessage("validation:minValue", { min: 0 })),
});

const PaletteProductSchema = z.object({
  volume: z.coerce
    .number({ message: i18nMessage("validation:required"), coerce: true })
    .min(0, i18nMessage("validation:minValue", { min: 0 })),
  bottleQuantity: z.coerce
    .number({ message: i18nMessage("validation:required"), coerce: true })
    .min(1, i18nMessage("validation:minValue", { min: 1 })),
});

const PaletteDetailSchema = z.object({
  palettePackages: z
    .array(PalettePackageSchema)
    .min(1, i18nMessage("boExpeditionForm:expeditionValidation.palettePackagesRequired")),
  paletteProducts: z
    .array(PaletteProductSchema)
    .min(1, i18nMessage("boExpeditionForm:expeditionValidation.productsRequired")),
});

const OtherDetailSchema = z.object({
  value: z.coerce
    .number({ message: i18nMessage("validation:required"), coerce: true })
    .min(0, i18nMessage("validation:minValue", { min: 0 })),
  numberOfExpedition: z.coerce
    .number({ message: i18nMessage("validation:required"), coerce: true })
    .min(1, i18nMessage("validation:minValue", { min: 1 })),
  isMultipleExpedition: z.boolean(),
  otherCustomQuotationType: z.nativeEnum(OtherCustomQuotationTypeEnum, {
    message: i18nMessage("validation:required"),
  }),
});

const CustomQuotationUpdateBoBaseFields = {
  ...ExpeditionUpdateBoSharedFields,
  pickUpLocationId: z.string().optional(),
  type: z.nativeEnum(CustomQuotationTypeEnum, {
    message: i18nMessage("boExpeditionForm:expeditionValidation.requestTypeRequired"),
  }),
  parcelDetail: ParcelDetailSchema.optional(),
  paletteDetail: PaletteDetailSchema.optional(),
  otherDetail: OtherDetailSchema.optional(),
};

export const CustomQuotationUpdateBoFormSchema = z
  .discriminatedUnion("apiEnum", [
    z.object({
      ...CustomQuotationUpdateBoBaseFields,
      apiEnum: z.literal("NONE"),
      expeditionWoApi: ExpeditionWoApiSchema,
      deliveryContent: z
        .array(DeliveryContentSchema)
        .min(1, i18nMessage("boExpeditionForm:expeditionValidation.productsRequired")),
    }),
    z.object({
      ...CustomQuotationUpdateBoBaseFields,
      apiEnum: z.literal("FEDEX"),
      manufacturerInfo: ManufacturerInfoSchema.optional(),
      expeditionFedexApi: ExpeditionFedexApiSchema,
      deliveryContent: z
        .array(DeliveryContentSchema)
        .min(1, i18nMessage("boExpeditionForm:expeditionValidation.productsRequired")),
    }),
    z.object({
      ...CustomQuotationUpdateBoBaseFields,
      apiEnum: z.literal("NONE_SIMPLIFIED"),
    }),
  ])
  .superRefine((data, ctx) => {
    if (data.type === CustomQuotationTypeEnum.PARCEL && !data.parcelDetail) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: i18nMessage("boExpeditionForm:expeditionValidation.parcelDetailRequired"),
        path: ["parcelDetail"],
      });
    }
    if (data.type === CustomQuotationTypeEnum.PALETTE && !data.paletteDetail) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: i18nMessage("boExpeditionForm:expeditionValidation.paletteDetailRequired"),
        path: ["paletteDetail"],
      });
    }
    if (data.type === CustomQuotationTypeEnum.OTHER && !data.otherDetail) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: i18nMessage("boExpeditionForm:expeditionValidation.otherDetailRequired"),
        path: ["otherDetail"],
      });
    }
    if (data.apiEnum === "FEDEX" || data.apiEnum === "NONE") {
      validateFedexPackagingWeightAgainstContent(data, ctx);
      validateInsuranceValueAgainstContent(data, ctx);
    }
  });

const FinalizeDetailsFields = {
  packagingInstructions: z.string().trim().optional(),
  packagingPriority: z.boolean(),
};

const TrackingFields = {
  trackingUrl: z
    .string()
    .trim()
    .url({ message: i18nMessage("boExpeditionForm:expeditionValidation.trackingUrlInvalid") })
    .optional()
    .or(z.literal("")),
  trackingCode: z
    .string()
    .trim()
    .regex(/^\d+$/, {
      message: i18nMessage("boExpeditionForm:expeditionValidation.trackingCodeDigitsOnly"),
    })
    .optional()
    .or(z.literal("")),
};

export const FinalizeExpeditionFormSchema = z.object({
  apiEnum: z.enum(["FEDEX", "NONE", "NONE_SIMPLIFIED"]),
  ...TrackingFields,
  ...FinalizeDetailsFields,
});

export const UpdateFinalizedQuotationFormSchema = z.discriminatedUnion("apiEnum", [
  z.object({
    apiEnum: z.literal("NONE"),
    ...QuotationUpdateBoBaseFields,
    ...TrackingFields,
    ...FinalizeDetailsFields,
  }),
  z.object({
    apiEnum: z.literal("FEDEX"),
    ...QuotationUpdateBoBaseFields,
    ...FinalizeDetailsFields,
  }),
]);

export const UpdateFinalizedCustomQuotationFormSchema = z
  .discriminatedUnion("apiEnum", [
    z.object({
      apiEnum: z.literal("NONE"),
      ...CustomQuotationUpdateBoBaseFields,
      deliveryContent: z
        .array(DeliveryContentSchema)
        .min(1, i18nMessage("boExpeditionForm:expeditionValidation.productsRequired")),
      ...TrackingFields,
      ...FinalizeDetailsFields,
    }),
    z.object({
      apiEnum: z.literal("FEDEX"),
      ...CustomQuotationUpdateBoBaseFields,
      deliveryContent: z
        .array(DeliveryContentSchema)
        .min(1, i18nMessage("boExpeditionForm:expeditionValidation.productsRequired")),
      ...FinalizeDetailsFields,
    }),
    z.object({
      apiEnum: z.literal("NONE_SIMPLIFIED"),
      ...CustomQuotationUpdateBoBaseFields,
      ...TrackingFields,
      ...FinalizeDetailsFields,
    }),
  ])
  .superRefine((data, ctx) => {
    if (data.type === CustomQuotationTypeEnum.PARCEL && !data.parcelDetail) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: i18nMessage("boExpeditionForm:expeditionValidation.parcelDetailRequired"),
        path: ["parcelDetail"],
      });
    }
    if (data.type === CustomQuotationTypeEnum.PALETTE && !data.paletteDetail) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: i18nMessage("boExpeditionForm:expeditionValidation.paletteDetailRequired"),
        path: ["paletteDetail"],
      });
    }
    if (data.type === CustomQuotationTypeEnum.OTHER && !data.otherDetail) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: i18nMessage("boExpeditionForm:expeditionValidation.otherDetailRequired"),
        path: ["otherDetail"],
      });
    }
  });

export type DeliveryContentData = z.infer<typeof DeliveryContentSchema>;
export type QuotationUpdateBoFormData = z.infer<typeof QuotationUpdateBoFormSchema>;
export type CustomQuotationUpdateBoFormData = z.infer<typeof CustomQuotationUpdateBoFormSchema>;
export type ExpeditionBoDeliveryInfoFormData =
  | QuotationUpdateBoFormData
  | CustomQuotationUpdateBoFormData
  | UpdateFinalizedQuotationFormData
  | UpdateFinalizedCustomQuotationFormData;
export type ExpeditionFedexApiData = z.infer<typeof ExpeditionFedexApiSchema>;
export type ManufacturerInfoData = z.infer<typeof ManufacturerInfoSchema>;
export type ExpeditionWoApiData = z.infer<typeof ExpeditionWoApiSchema>;
export type FinalizeExpeditionFormData = z.infer<typeof FinalizeExpeditionFormSchema>;
export type UpdateFinalizedQuotationFormData = z.infer<typeof UpdateFinalizedQuotationFormSchema>;
export type UpdateFinalizedCustomQuotationFormData = z.infer<
  typeof UpdateFinalizedCustomQuotationFormSchema
>;
